import {CAMPAIGN_NODE_ID, NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {DTCreativeNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {Nodes, NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {CardConnectionType} from '../cardsConnectionTool.enums';
import {ConnectionRequest} from '../cardsConnectionTool.types';
import wouldCreateLoop from './wouldCreateLoop';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';

export enum ConnectionResultStatus {
  Success = 'Success',
  Warning = 'Warning',
  Error = 'Error',
}

export interface ConnectionResult {
  status: ConnectionResultStatus;
  message?: string;
}

interface ProcessConnectionParams {
  connection: ConnectionRequest;
  storeNodes: Nodes;
  nodesToUpdate: Nodes;
}

/**
 * Validate and process a single connection request.
 * For card connections: mutates nodesToUpdate and the output's nodeId when valid.
 * For campaign connections: validates only (caller applies rootNodeId).
 */
export default function processConnection({
  connection,
  storeNodes,
  nodesToUpdate,
}: ProcessConnectionParams): ConnectionResult {
  const {sourceCardId} = connection;

  if (sourceCardId === CAMPAIGN_NODE_ID) {
    return processCampaignConnection(connection, storeNodes);
  }

  return processCardConnection(connection, storeNodes, nodesToUpdate);
}

function processCardConnection(
  connection: ConnectionRequest,
  storeNodes: Nodes,
  nodesToUpdate: Nodes,
): ConnectionResult {
  const {sourceCardId, sourceCardOutputId, targetCardId, type} = connection;

  const sourceCard = storeNodes[sourceCardId];

  if (!sourceCard) {
    return error(`Card ${idHelper.toCardId(sourceCardId)} doesn't exist`);
  }

  const output = sourceCard.outputs?.find((out) => out.id === sourceCardOutputId);

  if (!output) {
    return error(
      `Card ${idHelper.toCardId(sourceCardId)} doesn't have output ${idHelper.toOutputId(sourceCardOutputId)}`,
    );
  }

  if (type === CardConnectionType.Disconnect) {
    if (output.nodeId === NO_NODE_ID) {
      const sourceCardName = sourceCard.name ?? `card ${idHelper.toCardId(sourceCardId)}`;

      return warning(
        `Output ${idHelper.toOutputId(sourceCardOutputId)} in ${sourceCardName} is already disconnected`,
      );
    }

    nodesToUpdate[sourceCardId] = sourceCard;
    output.nodeId = NO_NODE_ID;

    return success();
  }

  // Connect operations
  const targetCard = storeNodes[targetCardId];

  if (!targetCard) {
    return error(`Card ${idHelper.toCardId(targetCardId)} doesn't exist`);
  }

  if (output.nodeId === targetCardId) {
    const targetCardName = targetCard.name ?? `card ${idHelper.toCardId(targetCardId)}`;
    const sourceCardName = sourceCard.name ?? `card ${idHelper.toCardId(sourceCardId)}`;

    return warning(
      `Output ${idHelper.toOutputId(sourceCardOutputId)} in ${sourceCardName} is already connected to ${targetCardName}`,
    );
  }

  if (wouldCreateLoop(sourceCardId, targetCardId, storeNodes)) {
    return error(
      `Cannot connect card ${idHelper.toCardId(sourceCardId)} output ${idHelper.toOutputId(sourceCardOutputId)} to card ${idHelper.toCardId(targetCardId)} because it creates an infinite loop`,
    );
  }

  // Fallback creative handling
  const isDestinationFallbackCreative =
    targetCard.type === NodeType.CREATIVE && (targetCard as DTCreativeNode).isFallbackCreative;

  if (isDestinationFallbackCreative) {
    return processFallbackCreativeConnection(
      connection,
      sourceCard,
      output,
      targetCard,
      nodesToUpdate,
    );
  }

  nodesToUpdate[sourceCardId] = sourceCard;
  output.nodeId = targetCardId;

  return success();
}

function processFallbackCreativeConnection(
  connection: ConnectionRequest,
  sourceCard: Nodes[number],
  output: Nodes[number]['outputs'][number],
  targetCard: Nodes[number],
  nodesToUpdate: Nodes,
): ConnectionResult {
  const {sourceCardId, sourceCardOutputId, targetCardId} = connection;
  const isFallbackOutput = output.type === OutputType.Fallback;

  if (!isFallbackOutput) {
    return error(
      `Cannot connect a fallback creative to a non-fallback output (card ${idHelper.toCardId(sourceCardId)} output ${idHelper.toOutputId(sourceCardOutputId)} to card ${idHelper.toCardId(targetCardId)})`,
    );
  }

  const sourceCardName = sourceCard.name ?? `card ${idHelper.toCardId(sourceCardId)}`;
  const destinationCardName = targetCard.name ?? `card ${idHelper.toCardId(targetCardId)}`;

  if (output.nodeId === NO_NODE_ID) {
    return warning(
      `Fallback output ${idHelper.toOutputId(sourceCardOutputId)} in ${sourceCardName} already leads to the fallback creative ${destinationCardName}, no change was done`,
    );
  }

  nodesToUpdate[sourceCardId] = sourceCard;
  output.nodeId = NO_NODE_ID;

  return success(
    `Output ${idHelper.toOutputId(sourceCardOutputId)} in ${sourceCardName} is connected to fallback creative ${destinationCardName} (this connection is invisible in the UI)`,
  );
}

function processCampaignConnection(
  connection: ConnectionRequest,
  storeNodes: Nodes,
): ConnectionResult {
  const {targetCardId, type} = connection;

  if (type === CardConnectionType.Connect) {
    const targetNode = storeNodes[targetCardId];

    if (!targetNode) {
      return error(`Target card ${idHelper.toCardId(targetCardId)} not found`);
    }

    if (
      targetNode.type === NodeType.CREATIVE &&
      (targetNode as DTCreativeNode).isFallbackCreative
    ) {
      return error(`Cannot connect campaign to a fallback creative (card ${idHelper.toCardId(targetCardId)})`);
    }
  }

  return success();
}

function success(message?: string): ConnectionResult {
  return {status: ConnectionResultStatus.Success, message};
}

function warning(message: string): ConnectionResult {
  return {status: ConnectionResultStatus.Warning, message};
}

function error(message: string): ConnectionResult {
  return {status: ConnectionResultStatus.Error, message};
}
